import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from '..';
import { couponsApi } from '../../api'; // <-- Make sure this exists
import {
  Cashback,
  CreateCashback,
  CashbackFilter,
  ResolvedResponse,
  CashbackTransaction, // Add this import
} from '../../types';
interface ICashbackState {
  loading: boolean;
  cashbacks: ResolvedResponse<Cashback[]>;
  cashback: Cashback | null;
  cashbackTransactions: CashbackTransaction[]; // Add this line
}
const initialState: ICashbackState = {
  loading: false,
  cashbacks: { data: [] },
  cashback: null,
  cashbackTransactions: [], // Add this line
};

export const cashbackSlice = createSlice({
  name: 'cashbacks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(createCashback.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createCashback.fulfilled, (state) => {
      state.loading = false;
      toast.success('Cashback created successfully.');
    });
    builder.addCase(createCashback.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getAllCashbackConfig.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllCashbackConfig.fulfilled, (state, { payload }) => {
      state.cashbacks = payload;
      state.loading = false;
    });
    builder.addCase(getAllCashbackConfig.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getAllActiveCashback.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllActiveCashback.fulfilled, (state, { payload }) => {
      state.cashbacks = { data: payload };
      state.loading = false;
    });
    builder.addCase(getAllActiveCashback.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getCashbackConfig.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCashbackConfig.fulfilled, (state, { payload }) => {
      state.cashback = payload;
      state.loading = false;
    });
    builder.addCase(getCashbackConfig.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getCashbackTransactions.pending, (state) => {
      state.loading = true;
      state.cashbackTransactions = [];
    });
    builder.addCase(getCashbackTransactions.fulfilled, (state, { payload }) => {
      state.cashbackTransactions = payload;
      state.loading = false;
    });
    builder.addCase(getCashbackTransactions.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getCashbackTransactionById.pending, (state) => {
      state.loading = true;
      state.cashbackTransactions = [];
    });
    builder.addCase(
      getCashbackTransactionById.fulfilled,
      (state, { payload }) => {
        state.cashbackTransactions = payload;
        state.loading = false;
      }
    );
    builder.addCase(getCashbackTransactionById.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getUserCashbackTransaction.pending, (state) => {
      state.loading = true;
      state.cashbackTransactions = [];
    });
    builder.addCase(
      getUserCashbackTransaction.fulfilled,
      (state, { payload }) => {
        state.cashbackTransactions = payload;
        state.loading = false;
      }
    );
    builder.addCase(getUserCashbackTransaction.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateCashback.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateCashback.fulfilled, (state) => {
      state.loading = false;
      toast.success('Cashback updated successfully.');
    });
    builder.addCase(updateCashback.rejected, (state) => {
      state.loading = false;
    });
  },
});

// Thunks (adjust API calls as needed)
const createCashback = createAsyncThunk(
  'createCashback',
  async (
    params: { payload: CreateCashback; cb?: () => void },
    { dispatch }
  ) => {
    try {
      const { payload, cb } = params;
      const { data } = await couponsApi.createCashback(payload);
      cb && cb();
      dispatch(getAllCashbackConfig());
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

const getAllCashbackConfig = createAsyncThunk('getAllCashbacks', async () => {
  try {
    const data = await couponsApi.getAllCashbackConfig();
    return data;
  } catch (error: any) {
    throw error;
  }
});

const getCashbackConfig = createAsyncThunk(
  'getCashback',
  async (param: { id: number }) => {
    try {
      const { data } = await couponsApi.getCashbackConfig(param.id);
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

const getCashbackTransactions = createAsyncThunk(
  'getCashbackTransactions',
  async () => {
    try {
      const response = await couponsApi.getCashbackTransactions();
      return response.cashBackUses;
    } catch (error: any) {
      throw error;
    }
  }
);

const getAllActiveCashback = createAsyncThunk(
  'getAllActiveCashback',
  async () => {
    try {
      const data = await couponsApi.getAllActiveCashback();
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

const getCashbackTransactionById = createAsyncThunk(
  'getCashbackTransactionById',
  async (param: { id: number; params?: CashbackFilter }) => {
    try {
      const data = await couponsApi.getCashbackTransactionById(
        param.id,
        param.params
      );
      return data.data;
    } catch (error: any) {
      throw error;
    }
  }
);

const getUserCashbackTransaction = createAsyncThunk(
  'getUserCashbackTransactionBy',
  async (param: { id: number }) => {
    try {
      const data = await couponsApi.getUserCashbackTransaction(param.id);
      return data.data;
    } catch (error: any) {
      throw error;
    }
  }
);

const updateCashback = createAsyncThunk(
  'updateCashback',
  async (
    params: {
      payload: CreateCashback;
      cb?: () => void;
    },
    { dispatch }
  ) => {
    try {
      const { payload, cb } = params;
      const { data } = await couponsApi.updateCashback(payload);
      cb && cb();
      dispatch(getAllCashbackConfig());
      return data.data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const cashbackSelector = (state: RootState) => state.cashback;
export {
  createCashback,
  getAllCashbackConfig,
  getCashbackConfig,
  getAllActiveCashback,
  getCashbackTransactions,
  getCashbackTransactionById,
  getUserCashbackTransaction,
  updateCashback,
};

export default cashbackSlice.reducer;
